import { emptyDraft, move, parseDrafts, toDraft, type ExerciseDraft, type ProgramDetail, type ProgramExercise } from '@gymtv/data';

export const COLS = ['name', 'sets', 'reps', 'weight', 'rest_s', 'notes'] as const;
export type Col = (typeof COLS)[number];

export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'invalid' | 'error';

/** Client-side state of one program day. `rev` increases on every edit so a save that
 *  finishes after newer edits does not mark the day as saved. */
export type DayState = {
  id: string;
  week: number;
  position: number;
  name: string;
  rows: ExerciseDraft[];
  rev: number;
  savedRev: number;
  status: SaveStatus;
  error: string | null;
  badRow: number | null;
};

let keyN = 0;
export const newKey = () => `r${++keyN}`;

export function daysFromProgram(p: ProgramDetail): Record<string, DayState> {
  const out: Record<string, DayState> = {};
  for (const d of p.program_days) {
    out[d.id] = {
      id: d.id,
      week: d.week,
      position: d.position,
      name: d.name,
      rows: d.program_exercises.map((e) => toDraft(e, newKey())),
      rev: 0,
      savedRev: 0,
      status: 'saved',
      error: null,
      badRow: null,
    };
  }
  return out;
}

export function daysOfWeek(days: Record<string, DayState>, week: number): DayState[] {
  return Object.values(days)
    .filter((d) => d.week === week)
    .sort((a, b) => a.position - b.position);
}

/** Applies an edit to a day's rows and marks it dirty (validation happens at save time). */
export function edit(day: DayState, rows: ExerciseDraft[]): DayState {
  return { ...day, rows, rev: day.rev + 1, status: 'dirty' };
}

export function updateCell(day: DayState, row: number, col: Col, value: string): DayState {
  return edit(day, day.rows.map((r, i) => (i === row ? { ...r, [col]: value } : r)));
}

export function addRow(day: DayState, at = day.rows.length, from?: ExerciseDraft): DayState {
  const base = from ? { ...from, key: newKey(), name: '' } : emptyDraft(newKey());
  const rows = day.rows.slice();
  rows.splice(at, 0, base);
  return edit(day, rows);
}

export function removeRow(day: DayState, row: number): DayState {
  return edit(day, day.rows.filter((_, i) => i !== row));
}

export function moveRow(day: DayState, from: number, to: number): DayState {
  return edit(day, move(day.rows, from, to));
}

/** Rows with a blank name and default values are ignored on save (a scratch row at the end). */
export function meaningfulRows(rows: ExerciseDraft[]): ExerciseDraft[] {
  return rows.filter((r) => r.name.trim() !== '' || r.notes.trim() !== '' || r.weight.trim() !== '');
}

export type Validated = { ok: true; exercises: ProgramExercise[] } | { ok: false; error: string; row: number };

export function validateDay(day: DayState): Validated {
  const rows = meaningfulRows(day.rows);
  const res = parseDrafts(rows);
  if (res.ok) return res;
  // map the row index back to the visible grid
  return { ok: false, error: res.error, row: day.rows.indexOf(rows[res.row]!) };
}

/** Keyboard navigation in a day grid. Returns the next cell, or null to let the input handle the key. */
export function navigate(
  key: string,
  pos: { row: number; col: number },
  rows: number,
  caret: { atStart: boolean; atEnd: boolean },
): { row: number; col: number } | null {
  const cols = COLS.length;
  switch (key) {
    case 'ArrowUp':
      return pos.row > 0 ? { row: pos.row - 1, col: pos.col } : null;
    case 'ArrowDown':
    case 'Enter':
      return pos.row < rows - 1 ? { row: pos.row + 1, col: pos.col } : null;
    case 'ArrowLeft':
      return caret.atStart && pos.col > 0 ? { row: pos.row, col: pos.col - 1 } : null;
    case 'ArrowRight':
      return caret.atEnd && pos.col < cols - 1 ? { row: pos.row, col: pos.col + 1 } : null;
    default:
      return null;
  }
}

/** Progression matrix for one day position: rows = exercise slots, columns = weeks. */
export type ProgCell = { dayId: string; row: number; draft: ExerciseDraft } | null;
export type ProgRow = { label: string; cells: ProgCell[] };

export function progression(days: Record<string, DayState>, position: number, weeks: number): ProgRow[] {
  const byWeek: Array<DayState | undefined> = [];
  for (let w = 1; w <= weeks; w++) byWeek.push(Object.values(days).find((d) => d.week === w && d.position === position));
  const slots = Math.max(0, ...byWeek.map((d) => (d ? d.rows.length : 0)));
  const out: ProgRow[] = [];
  for (let i = 0; i < slots; i++) {
    const cells: ProgCell[] = byWeek.map((d) => (d && d.rows[i] ? { dayId: d.id, row: i, draft: d.rows[i]! } : null));
    const label = cells.find((c) => c && c.draft.name.trim())?.draft.name ?? `Exercise ${i + 1}`;
    out.push({ label, cells });
  }
  return out;
}

export function dirtyDays(days: Record<string, DayState>): DayState[] {
  return Object.values(days).filter((d) => d.status === 'dirty' || d.status === 'error');
}

export function overallStatus(days: Record<string, DayState>): SaveStatus {
  const all = Object.values(days);
  if (all.some((d) => d.status === 'error')) return 'error';
  if (all.some((d) => d.status === 'invalid')) return 'invalid';
  if (all.some((d) => d.status === 'saving')) return 'saving';
  if (all.some((d) => d.status === 'dirty')) return 'dirty';
  return 'saved';
}
